import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Input from '../../components/Form/Input';
import Button from '../../components/Button';
import LoadingPanel from '../../components/LoadingPanel';
import { usePageContent } from '../../hooks/usePageContent';
import styles from './index.module.css';

const SEARCH_URL = import.meta.env.VITE_FUNNELBACK_SEARCH_URL;
const SUGGEST_URL = import.meta.env.VITE_FUNNELBACK_SUGGEST_URL;
const PAGE_SIZE = 10;

function getSuggestionLabel(s) {
  if (typeof s === 'string') return s;
  return s.disp || s.label || s.key || '';
}

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { find } = usePageContent('search-results');
  const c = find('searchResults') || {};

  const query = searchParams.get('query') || '';
  const sort = searchParams.get('sort') || 'relevance';
  const startRank = parseInt(searchParams.get('start_rank') || '1', 10);

  const [inputValue, setInputValue] = useState(query);
  const [resultPacket, setResultPacket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  useEffect(() => {
    if (!query) {
      setResultPacket(null);
      return;
    }
    setLoading(true);
    setError(false);

    const params = new URLSearchParams({ query, sort, start_rank: startRank });
    const searchSeparator = SEARCH_URL.includes('?') ? '&' : '?';

    fetch(`${SEARCH_URL}${searchSeparator}${params}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.json();
      })
      .then((data) => setResultPacket(data?.response?.resultPacket ?? null))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [query, sort, startRank]);

  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setSuggestions([]);
      return;
    }
    const suggestSeparator = SUGGEST_URL.includes('?') ? '&' : '?';
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${SUGGEST_URL}${suggestSeparator}partial_query=${encodeURIComponent(inputValue)}`);
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const navigate = (newParams) => {
    setSearchParams({ query, sort, start_rank: 1, ...newParams });
    setSuggestions([]);
    setSuggestionsVisible(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    navigate({ query: inputValue.trim(), start_rank: 1 });
  };

  const handleSortChange = (e) => {
    navigate({ sort: e.target.value, start_rank: 1 });
  };

  const handleSuggestionClick = (s) => {
    const text = getSuggestionLabel(s);
    setInputValue(text);
    navigate({ query: text, start_rank: 1 });
  };

  const goToPage = (newStartRank) => {
    setSearchParams({ query, sort, start_rank: newStartRank });
    window.scrollTo(0, 0);
  };

  const summary = resultPacket?.resultsSummary;
  const resultItems = resultPacket?.results ?? [];
  const spell = resultPacket?.spell;

  const totalPages = summary ? Math.ceil(summary.totalMatching / PAGE_SIZE) : 0;
  const currentPage = Math.ceil(startRank / PAGE_SIZE);

  const PAGE_WINDOW = 5;
  const windowEnd = Math.min(totalPages, Math.max(PAGE_WINDOW, currentPage + Math.floor(PAGE_WINDOW / 2)));
  const windowStart = Math.max(1, windowEnd - PAGE_WINDOW + 1);
  const visiblePages = Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i);

  return (
    <div id="content" className={styles.page}>
      <div className="page-header">
        <div className="page-header__content">
          <h1 className="page-header__title">{c.pageTitle || 'Search Results'}</h1>
        </div>
      </div>

      <div className="content-holder">
        <div className="search-results">

          <div className="search-results__row">
            <form className="search-results__search-form" role="search" onSubmit={handleSubmit}>
              <div className={styles.inputWrapper}>
                <Input
                  id="vplates-site-search"
                  name="query"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onFocus={() => setSuggestionsVisible(true)}
                  onBlur={() => setTimeout(() => setSuggestionsVisible(false), 150)}
                  placeholder={c.inputPlaceholder || 'Search…'}
                  aria-label={c.inputAriaLabel || 'Search'}
                  autoComplete="off"
                />
                {suggestionsVisible && suggestions.length > 0 && (
                  <div className="search-results__suggestions visible">
                    <ul className="search-results__suggestions-list">
                      {suggestions.map((s, i) => (
                        <li key={i} className="search-results__suggestions-list-item">
                          <button
                            type="button"
                            className={`search-results__suggestions-list-item-link ${styles.suggestionBtn}`}
                            onMouseDown={() => handleSuggestionClick(s)}
                          >
                            {getSuggestionLabel(s)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <Button type="submit">{c.searchButtonText || 'Search'}</Button>
            </form>
          </div>

          {loading && <LoadingPanel />}

          {!loading && error && (
            <div className="error-bar" role="alert">
              {c.errorText || 'Unable to retrieve search results at this time. Please try again later.'}
            </div>
          )}

          {!loading && !error && resultPacket && (
            <>
              {spell?.text && (
                <p className="search-results__spelling-text">
                  {c.didYouMeanPrefix || 'Did you mean:'}{' '}
                  <button
                    type="button"
                    className={`search-results__spelling-text-link ${styles.suggestionBtn}`}
                    style={{ width: 'auto' }}
                    onClick={() => navigate({ query: spell.text, start_rank: 1 })}
                  >
                    {spell.text}
                  </button>
                </p>
              )}

              <div className="search-results__row">
                <div className="search-results__counter">
                  <span className="search-results__counter-text">
                    {summary.totalMatching > 0
                      ? (c.resultsCountTemplate || '{start} – {end} of {total} search results')
                          .replace('{start}', summary.currStart)
                          .replace('{end}', summary.currEnd)
                          .replace('{total}', summary.totalMatching)
                      : `${c.noResultsForPrefix || 'No results found for'} "${query}"`}
                  </span>
                </div>

                <div className="search-results__sort">
                  <label className="input__label" htmlFor="search-results-sort-type">
                    {c.sortLabel || 'Sort by'}
                  </label>
                  <select
                    id="search-results-sort-type"
                    className="input__ctrl input__ctrl--select"
                    value={sort}
                    onChange={handleSortChange}
                  >
                    <option value="relevance">{c.sortRelevance || 'Relevance'}</option>
                    <option value="date">{c.sortDate || 'Date (Newest first)'}</option>
                    <option value="title">{c.sortTitle || 'Title (A-Z)'}</option>
                  </select>
                </div>
              </div>

              {resultItems.length === 0 ? (
                <div className="search-results__empty">
                  <p>{c.emptyResultsText || 'No results found. Try a different search term.'}</p>
                </div>
              ) : (
                <div className="search-results__results">
                  {resultItems.map((result) => (
                    <div key={result.rank} className="search-results__result">
                      <h3 className="search-results__result-title">
                        <a
                          className="search-results__result-title-link"
                          href={result.liveUrl}
                          title={result.liveUrl}
                        >
                          {result.title}
                        </a>
                      </h3>
                       {result.liveUrl}
                      {result.summary && (
                        <p
                          className="search-results__result-description"
                          dangerouslySetInnerHTML={{ __html: result.summary }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {totalPages >= 1 && (
                <div className="search-results__pagination">
                  {currentPage > 1 && (
                    <Button
                      variant="tertiary"
                      className="prev-page"
                      onClick={() => goToPage((currentPage - 2) * PAGE_SIZE + 1)}
                    >
                      {c.previousText || 'Previous'}
                    </Button>
                  )}
                  <ul className="search-results__pagination-list">
                    {visiblePages.map((page) => {
                      const isActive = page === currentPage;
                      const linkClass = [
                        'search-results__pagination-list-item-link',
                        isActive && 'search-results__pagination-list-item-link-active',
                        styles.paginationBtn,
                      ]
                        .filter(Boolean)
                        .join(' ');
                      return (
                        <li key={page} className="search-results__pagination-list-item">
                          <button
                            type="button"
                            className={linkClass}
                            onClick={() => goToPage((page - 1) * PAGE_SIZE + 1)}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {currentPage < totalPages && (
                    <Button
                      variant="tertiary"
                      onClick={() => goToPage(currentPage * PAGE_SIZE + 1)}
                    >
                      {c.nextText || 'Next'}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {!loading && !error && !resultPacket && !query && (
            <p>{c.initialPromptText || 'Enter a search term above to find results.'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
